import { inject, Injectable } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { MediaHubApiService } from './media-hub-api.service'
import { Project } from '@models/project.model'

export interface CreateProjectRequest {
  name: string
  description?: string
  thumbnailUrl?: string
  tags?: string[]
  mediaAssetIds?: string[]
  mediaAssetChatIds?: string[]
  playlistIds?: string[]
  generalChatIds?: string[]
  mediaLibraryAssetIds?: string[]
}

export interface UpdateProjectRequest {
  name?: string
  description?: string
  thumbnailUrl?: string
  tags?: string[]
}

export interface AddAssetsToProjectRequest {
  assetIds: string[]
  mediaLibraryAssetIds?: string[]
}

export interface RemoveAssetsFromProjectRequest {
  assetIds: string[]
}

export interface AddMediaAssetChatsToProjectRequest {
  mediaAssetChatIds: string[]
}

export interface RemoveMediaAssetChatsFromProjectRequest {
  mediaAssetChatIds: string[]
}

export interface AddPlaylistsToProjectRequest {
  playlistIds: string[]
}

export interface RemovePlaylistsFromProjectRequest {
  playlistIds: string[]
}

export interface AddGeneralChatsToProjectRequest {
  generalChatIds: string[]
}

export interface RemoveGeneralChatsFromProjectRequest {
  generalChatIds: string[]
}

export interface ProjectResponse {
  success?: boolean
  message?: string
  project: Project
  addedCount?: number
}

export interface ProjectsResponse {
  success?: boolean
  projects: Project[]
  count?: number
}

export interface DeleteProjectResponse {
  success: boolean
  message: string
}

@Injectable({
  providedIn: 'root',
})
export class ProjectApiService {
  private apiService = inject(MediaHubApiService)
  private readonly endpoint = '/projects'

  /**
   * Creates a new project. Optionally include initial media assets, chats, and playlists.
   */
  createProject(request: CreateProjectRequest): Observable<ProjectResponse> {
    return this.apiService.postBaseRx<CreateProjectRequest, ProjectResponse>(this.endpoint, request)
  }

  /**
   * Retrieves all projects for the authenticated user.
   */
  getAllProjects(): Observable<ProjectsResponse> {
    return this.apiService.getBaseRx<ProjectsResponse>(this.endpoint)
  }

  /**
   * Retrieves projects filtered by tags.
   * @param tags - Array of tags to filter by
   */
  getProjectsByTags(tags: string[]): Observable<ProjectsResponse> {
    const params = new HttpParams().set('tags', tags.join(','))
    return this.apiService.getBaseRx<ProjectsResponse>(this.endpoint, params)
  }

  /**
   * Retrieves a specific project by ID with all associated resources.
   * @param projectId - The ID of the project to retrieve
   */
  getProjectById(projectId: string): Observable<ProjectResponse> {
    return this.apiService.getBaseRx<ProjectResponse>(`${this.endpoint}/${projectId}`)
  }

  /**
   * Updates project metadata. All fields are optional. Only provided fields will be updated.
   * @param projectId - The ID of the project to update
   * @param request - The fields to update
   */
  updateProject(projectId: string, request: UpdateProjectRequest): Observable<DeleteProjectResponse> {
    return this.apiService.putBaseRx<UpdateProjectRequest, DeleteProjectResponse>(`${this.endpoint}/${projectId}`, request)
  }

  /**
   * Adds one or more media assets to a project. Duplicates are automatically prevented.
   * @param projectId - The ID of the project
   * @param assetIds - Array of asset IDs to add
   * @param mediaLibraryAssetIds - Optional array of media library asset IDs to add
   */
  addAssetsToProject(projectId: string, assetIds: string[], mediaLibraryAssetIds?: string[]): Observable<ProjectResponse> {
    const request: AddAssetsToProjectRequest = { assetIds, mediaLibraryAssetIds }
    return this.apiService.postBaseRx<AddAssetsToProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/assets`, request)
  }

  /**
   * Removes one or more media assets from a project.
   * @param projectId - The ID of the project
   * @param assetIds - Array of asset IDs to remove
   */
  removeAssetsFromProject(projectId: string, assetIds: string[]): Observable<ProjectResponse> {
    const request: RemoveAssetsFromProjectRequest = { assetIds }
    return this.apiService.deleteBaseWithBodyRx<RemoveAssetsFromProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/assets`, request)
  }

  /**
   * Adds one or more media asset chats to a project.
   * @param projectId - The ID of the project
   * @param mediaAssetChatIds - Array of media asset chat IDs to add
   */
  addMediaAssetChatsToProject(projectId: string, mediaAssetChatIds: string[]): Observable<ProjectResponse> {
    const request: AddMediaAssetChatsToProjectRequest = { mediaAssetChatIds }
    return this.apiService.postBaseRx<AddMediaAssetChatsToProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/media-asset-chats`, request)
  }

  /**
   * Removes one or more media asset chats from a project.
   * @param projectId - The ID of the project
   * @param mediaAssetChatIds - Array of media asset chat IDs to remove
   */
  removeMediaAssetChatsFromProject(projectId: string, mediaAssetChatIds: string[]): Observable<ProjectResponse> {
    const request: RemoveMediaAssetChatsFromProjectRequest = { mediaAssetChatIds }
    return this.apiService.deleteBaseWithBodyRx<RemoveMediaAssetChatsFromProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/media-asset-chats`, request)
  }

  /**
   * Adds one or more playlists to a project.
   * @param projectId - The ID of the project
   * @param playlistIds - Array of playlist IDs to add
   */
  addPlaylistsToProject(projectId: string, playlistIds: string[]): Observable<ProjectResponse> {
    const request: AddPlaylistsToProjectRequest = { playlistIds }
    return this.apiService.postBaseRx<AddPlaylistsToProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/playlists`, request)
  }

  /**
   * Removes one or more playlists from a project.
   * @param projectId - The ID of the project
   * @param playlistIds - Array of playlist IDs to remove
   */
  removePlaylistsFromProject(projectId: string, playlistIds: string[]): Observable<ProjectResponse> {
    const request: RemovePlaylistsFromProjectRequest = { playlistIds }
    return this.apiService.deleteBaseWithBodyRx<RemovePlaylistsFromProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/playlists`, request)
  }

  /**
   * Adds one or more general chats to a project.
   * @param projectId - The ID of the project
   * @param generalChatIds - Array of general chat IDs to add
   */
  addGeneralChatsToProject(projectId: string, generalChatIds: string[]): Observable<ProjectResponse> {
    const request: AddGeneralChatsToProjectRequest = { generalChatIds }
    return this.apiService.postBaseRx<AddGeneralChatsToProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/general-chats`, request)
  }

  /**
   * Removes one or more general chats from a project.
   * @param projectId - The ID of the project
   * @param generalChatIds - Array of general chat IDs to remove
   */
  removeGeneralChatsFromProject(projectId: string, generalChatIds: string[]): Observable<ProjectResponse> {
    const request: RemoveGeneralChatsFromProjectRequest = { generalChatIds }
    return this.apiService.deleteBaseWithBodyRx<RemoveGeneralChatsFromProjectRequest, ProjectResponse>(`${this.endpoint}/${projectId}/general-chats`, request)
  }

  /**
   * Permanently deletes a project. Note: This does not delete the associated resources (assets, chats, playlists), only the project container.
   * @param projectId - The ID of the project to delete
   */
  deleteProject(projectId: string): Observable<DeleteProjectResponse> {
    return this.apiService.deleteBaseRx<DeleteProjectResponse>(`${this.endpoint}/${projectId}`)
  }
}
